import IComptage from '@/types/comptage.interface';
import IRequestComptage from '@/types/request-comptage.interface';
import http from './api.service';
import ISolvability from "@/types/solvability.interface";
import i18n from "@/plugins/vue-i18n";

class ComptageService {
    mapFilters = {
        activity: {
            type: 'array',
            field: 'naf_sous_classe'
        },
        legal: {
            type: 'array',
            field: 'categorie_juridique'
        },
        geolocation: {
            type: 'geolocation',
            fields: ['region', 'departement', 'ville', 'zip_code']
        },
        score: {
            type: 'rangearray',
            field: 'score'
        },
        // TODO : add when fields are available in database
        /*
        scoreTrendDown: {
            type: 'bool',
            field: '???'
        },
        scoreTrendMid: {
            type: 'bool',
            field: '???'
        },
        scoreTrendUp: {
            type: 'bool',
            field: '???'
        },
         */
        date_immatriculation: {
            type: 'range',
            field: 'date_immatriculation'
        },
        hasPhone: {
            type: 'bool',
            field: 'telephone'
        },
        hasGroup: {
            type: 'bool',
            field: 'groupe'
        },
        // TODO : add when fields are available in database
        /*
        hasEmail: {
            type: 'bool',
            field: '???'
        },
        hasDirector: {
            type: 'bool',
            field: '???'
        },
        hasOperationalDirector: {
            type: 'bool',
            field: '???'
        },
         */
        ca_groupe: {
            type: 'range',
            field: 'ca_groupe'
        },
        effectif_groupe: {
            type: 'range',
            field: 'effectif_groupe'
        },
        ca_bilan: {
            type: 'range',
            field: 'ca_bilan'
        },
        effectif_bilan: {
            type: 'range',
            field: 'effectif_bilan'
        },
        date_creation_bilan: {
            type: 'range',
            field: 'date_creation_bilan'
        }
    }

    transformFiltersFunction = {
        bool: function (element: boolean) {
            return element;
        },
        range: function (element: { min?: number | string; max?: number | string; value?: number | string }) {
            return element;
        },
        array: function (elements: any[]) {
            return elements.map((element) => {
                if (Object.prototype.hasOwnProperty.call(element, 'naf_sous_classe_id')) {
                    return element.naf_sous_classe_id;
                } else if (Object.prototype.hasOwnProperty.call(element, 'naf_groupe_id')) {
                    return element.naf_groupe_id;
                } else if (Object.prototype.hasOwnProperty.call(element, 'naf_section_id')) {
                    return element.naf_section_id;
                } else if (Object.prototype.hasOwnProperty.call(element, 'categorie_juridique_niveau_3_id')) {
                    return element.categorie_juridique_niveau_3_id;
                } else if (Object.prototype.hasOwnProperty.call(element, 'categorie_juridique_niveau_1_id')) {
                    return element.categorie_juridique_niveau_1_id;
                }
                throw Error;
            });
        },
        rangearray: function (element: ISolvability) {
            const solvabilityFilter = [];
            if (element.ceasedActivity) {
                solvabilityFilter.push({value: 0});
            }
            if (element.collectiveProcedure) {
                solvabilityFilter.push({value: 1});
            }
            if (element.certifiedRisk) {
                solvabilityFilter.push({value: 2});
            }
            if (element.veryHighRisk) {
                solvabilityFilter.push({min: 3, max: 5});
            }
            if (element.highRisk) {
                solvabilityFilter.push({min: 6, max: 8});
            }
            if (element.mediumRisk) {
                solvabilityFilter.push({min: 9, max: 12});
            }
            if (element.lowRisk) {
                solvabilityFilter.push({min: 13, max: 16});
            }
            if (element.veryLowRisk) {
                solvabilityFilter.push({min: 17, max: 20});
            }
            return solvabilityFilter;
        },
        geolocation: function (elements) {
            const res = {
                geolocation: {
                    region: [],
                    departement: [],
                    ville: [],
                    zip_code: []
                }
            };
            for (const element of elements) {
                if (element.type === 'ville') {
                    res.geolocation[element.type].push(element.ville_id);
                } else if (element.type === 'departement') {
                    res.geolocation[element.type].push(element.departement_id);
                } else if (element.type === 'region') {
                    res.geolocation[element.type].push(element.region_id);
                } else if (element.type === 'zip_code') {
                    res.geolocation[element.type].push(element.zip_code_id);
                }
            }
            return res;
        }
    }

    getChildren(item): any[] {
        if (item.children) {
            if (item.children.length > 0 && item.children[0].children) {
                return [].concat(...item.children.map(a => a.children));
            } else {
                return item.children;
            }
        } else {
            return [item];
        }
    }

    transformToRequest(comptage: IComptage): IRequestComptage {
        const request: IRequestComptage = {
            type: comptage.type,
            filters: {},
            files: comptage.files,
            datas: comptage.datas
        };

        for (const key in comptage.filters) {
            if (Object.prototype.hasOwnProperty.call(comptage.filters, key)) {
                const element = comptage.filters[key];
                if (Object.prototype.hasOwnProperty.call(this.mapFilters, key)) {
                    const mapElement = this.mapFilters[key];
                    if (key == 'geolocation') {
                        const tmp = this.transformFiltersFunction[mapElement.type](element);
                        request.filters = {...request.filters, ...tmp};
                    } else {
                        request.filters[mapElement.field] = this.transformFiltersFunction[mapElement.type](element);
                    }
                }
            }
        }
        return request;
    }

    getNbCounted(comptage: IComptage) {
        if (comptage.type == 'entreprise') {
            return http.post('/comptage/entreprise', this.transformToRequest(comptage),
                {
                    headers: {'Content-Type': 'application/json'}
                })
        } else if (comptage.type == 'etablissement') {
            return http.post('/comptage/etablissement', this.transformToRequest(comptage),
                {
                    headers: {'Content-Type': 'application/json'}
                });
        }
    }

    preview(comptage: IComptage) {
        if (comptage.type == 'entreprise') {
            return http.post('/comptage/entreprise/preview', this.transformToRequest(comptage),
                {
                    headers: {'Content-Type': 'application/json'}
                })
        } else if (comptage.type == 'etablissement') {
            return http.post('/comptage/etablissement/preview', this.transformToRequest(comptage),
                {
                    headers: {'Content-Type': 'application/json'}
                });
        }
    }

    order(comptage: IComptage, basket, user) {
        if (comptage.type == 'entreprise') {

            return http.post('/comptage/entreprise/order', {...this.transformToRequest(comptage), ...{basket: {...basket, ...{email: user.email}}}},
                {
                    headers: {'Content-Type': 'application/json'},
                    params: {userId: user.id}
                })
        } else if (comptage.type == 'etablissement') {
            return http.post('/comptage/etablissement/order', {...this.transformToRequest(comptage), ...{basket: {...basket, ...{email: user.email}}}},
                {
                    headers: {'Content-Type': 'application/json'},
                    params: {userId: user.id}
                });
        }
    }

    solvabilityMap() {
        return {
            ceasedActivity: i18n.tc('comptage.filters.ceasedActivity'),
            collectiveProcedure: i18n.tc('comptage.filters.procedureCollect'),
            certifiedRisk: i18n.tc('comptage.filters.confirmedRisk'),
            veryHighRisk: i18n.tc('comptage.filters.veryHighRisk'),
            highRisk: i18n.tc('comptage.filters.highRisk'),
            mediumRisk: i18n.tc('comptage.filters.mediumRisk'),
            lowRisk: i18n.tc('comptage.filters.lowRisk'),
            veryLowRisk: i18n.tc('comptage.filters.veryLowRisk')
        }
    }

    keyNumbersMap() {
        return {
            ca_groupe: {
                translation: i18n.tc('comptage.filters.keyNumbers.ca_groupe'),
                type: 'number'
            },
            ca_bilan: {
                translation: i18n.tc('comptage.filters.keyNumbers.ca_bilan'),
                type: 'number'
            },
            effectif_bilan: {
                translation: i18n.tc('comptage.filters.keyNumbers.effectif_bilan'),
                type: 'number'
            },
            effectif_groupe: {
                translation: i18n.tc('comptage.filters.keyNumbers.effectif_groupe'),
                type: 'number'
            },
            date_immatriculation: {
                translation: i18n.tc('comptage.filters.keyNumbers.date_immatriculation'),
                type: 'date'
            },
            date_creation_bilan: {
                translation: i18n.tc('comptage.filters.keyNumbers.date_creation_bilan'),
                type: 'date'
            }
        }
    }

    getKeyNumbersValueSentence(key, value): string {
        const keyNumbersMap = this.keyNumbersMap();
        if (value) {
            return keyNumbersMap[key].type === 'number' ? keyNumbersMap[key].translation + ' ' + i18n.tc('equal.m') + ' ' + value : keyNumbersMap[key].translation + ' ' + i18n.tc('the.m') + ' ' + value
        } else {
            return null;
        }
    }

    getKeyNumbersRangeSentence(key, min, max): string {
        let description: string = null;
        const keyNumbersMap = this.keyNumbersMap();
        if (min && max) {
            const between = keyNumbersMap[key].type === 'number' ? i18n.tc('between') : i18n.tc('after');
            const and = keyNumbersMap[key].type === 'number' ? i18n.tc('and') : i18n.tc('and') + ' ' + i18n.tc('before');
            description = keyNumbersMap[key].translation + ' ' + between + ' ' + min + ' ' + and + ' ' + max;
        } else if (min) {
            const more = keyNumbersMap[key].type === 'number' ? i18n.tc('more') : i18n.tc('after');
            description = keyNumbersMap[key].translation + ' ' + more + ' ' + min;
        } else if (max) {
            const less = keyNumbersMap[key].type === 'number' ? i18n.tc('less') : i18n.tc('before');
            description = keyNumbersMap[key].translation + ' ' + less + ' ' + max;
        }
        return description;
    }

    getAdvancedMap() {
        return {
            hasPhone: i18n.tc('comptage.filters.hasPhone'),
            hasEmail: i18n.tc('comptage.filters.hasEmail'),
            hasGroup: i18n.tc('comptage.filters.hasGroup'),
            hasDirector: i18n.tc('comptage.filters.hasDirector'),
            hasOperationalDirector: i18n.tc('comptage.filters.hasOperationalDirector')

        }
    }
}

// A singleton instance
export default new ComptageService();
